import { useEffect, useRef } from "react";

interface SearchUrlSyncOptions {
  /**
   * Current search value
   */
  search: string;

  /**
   * Function to update search value
   */
  setSearch: (value: string) => void;

  /**
   * Navigate function from TanStack Router
   */
  navigate: (options: { search: (prev: any) => any; replace?: boolean }) => void;

  /**
   * Current URL search params
   */
  currentSearch: {
    search?: string;
    [key: string]: any;
  };

  /**
   * Debounce delay in milliseconds before syncing to URL
   * @default 300
   */
  debounceMs?: number;
}

/**
 * Custom hook to sync search input with URL parameters (truly bidirectional)
 *
 * This enables:
 * - Shareable URLs with search queries
 * - Persistent search state on page reload
 * - Debounced URL updates (avoids updating on every keystroke)
 * - Browser back/forward button support (URL changes update local state)
 * - Link navigation with search (local state updates when URL param changes)
 *
 * @example
 * ```tsx
 * const { search: urlSearch } = route.useSearch();
 * const navigate = route.useNavigate();
 * const [search, setSearch] = useState("");
 *
 * useSearchUrlSync({
 *   search,
 *   setSearch,
 *   navigate,
 *   currentSearch: { search: urlSearch }
 * });
 * ```
 */
export function useSearchUrlSync({
  search,
  setSearch,
  navigate,
  currentSearch,
  debounceMs = 300,
}: SearchUrlSyncOptions) {
  const isInitialMount = useRef(true);
  const lastSyncedSearch = useRef<string | undefined>(undefined);
  const debounceTimer = useRef<NodeJS.Timeout | undefined>(undefined);
  const navigateRef = useRef(navigate);
  const currentSearchRef = useRef(currentSearch);

  // Keep refs up to date
  navigateRef.current = navigate;
  currentSearchRef.current = currentSearch;

  // Extract urlSearch value to avoid object recreation issues
  const urlSearchValue = currentSearch.search;

  // Bidirectional sync: URL → Local State (always react to URL changes)
  useEffect(() => {
    // Check if URL search has actually changed
    const searchChanged = urlSearchValue !== lastSyncedSearch.current;

    if (!searchChanged) return;

    // Update tracking ref
    lastSyncedSearch.current = urlSearchValue;

    // Update local state to match URL
    if (urlSearchValue !== undefined && urlSearchValue !== search) {
      setSearch(urlSearchValue);
    } else if (urlSearchValue === undefined && search !== "") {
      // URL has no search but local state does → clear local state
      setSearch("");
    }
  }, [urlSearchValue, search, setSearch]);

  // Bidirectional sync: Local State → URL (debounced, when user types)
  useEffect(() => {
    // Skip on initial mount to avoid overwriting URL params
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Clear any existing debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Debounce the URL update
    debounceTimer.current = setTimeout(() => {
      // Only update if search value changed
      if (search !== urlSearchValue) {
        lastSyncedSearch.current = search || undefined;

        if (search.trim()) {
          // Non-empty search - add to URL
          navigate({
            search: (prev) => ({
              ...prev,
              search: search.trim(),
            }),
            replace: true,
          });
        } else {
          // Empty search - remove from URL
          if (urlSearchValue) {
            navigate({
              search: (prev) => {
                const { search: _unused, ...rest } = prev;
                return rest;
              },
              replace: true,
            });
          }
        }
      }
    }, debounceMs);

    // Cleanup function
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [search, navigate, urlSearchValue, debounceMs]);
}
